import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { map, switchMap } from 'rxjs';

import { DiscrepancyService } from '@services/api/discrepancy/discrepancy.service';
import { AutomationService } from '@services/api/automation/automation.service';
import { SnackbarService } from '@services/snackbar.service';
import { StationService } from '@services/api/station/station.service';
import { DocumentService } from '@services/api/document/document.service';
import { WorkflowService } from '@services/api/workflow/workflow.service';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { LoaderComponent } from '@shared/loader/loader.component';
import { SelectComponent } from '@shared/select/select.component';
import { InputComponent } from '@shared/input/input.component';
import { DatepickerComponent } from '@shared/datepicker/datepicker.component';
import { InfoBoxComponent } from '@shared/info-box/info-box.component';
import { TextareaComponent } from '@shared/textarea/textarea.component';
import { PageRangeFormatErrorComponent } from '@shared/validation-error/page-range-format-error.component';
import { PageRangeConflictErrorComponent } from '@shared/validation-error/page-range-conflict-error.component';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import {
    pageRangeFormatValidator,
    optionExistsValidator,
} from '@helpers/validators';
import { deepEqual } from '@helpers/index';
import { mapKeysToCamelCase } from '@helpers/string-utils';
import {
    getCustomColumnInputType,
    getCustomColumnLabel,
    getCustomColumnOptions,
    isCustomColumnDate,
    isCustomColumnListBox,
    normalizeCustomColumnValues,
} from '@helpers/custom-column.helpers';
import { ErrorResponse } from '@models/api.model';
import { SelectOption } from '@models/shared.model';
import { DiscrepancyDetail, PageConflict } from '@models/discrepancy';
import { DocDiscrepancyCreateOptions } from '@models/document/discrepancy-create-options';
import { CustomColumn } from '@models/workflow/custom-column';
import {
    AutomationFieldKind,
    AutomationRule,
    AutomationRuleField,
    AutomationRuleTypeGroup,
} from '@models/automation';
import {
    AutomationOptionContext,
    AutomationTarget,
    automationFieldIdsSharingTarget,
    automationInputFieldIdsOfKind,
    automationInputFieldIdsSharingTarget,
    automationInputFields,
    automationOutputFieldIdsOfKind,
    automationOutputFieldIdsSharingTarget,
    automationOutputFields,
    flattenAutomationRules,
    resolveAutomationTarget,
    toAutomationRuleValue,
    toCustomColumnControlValue,
} from '@helpers/automation-field.helpers';
import {
    findOptionByRuleValue,
    isNameBasedValueKind,
} from '@helpers/automation-option-value';
import {
    AutomationCascadeGraph,
    AutomationPreparedWrite,
    buildAutomationCascadeGraph,
    downstreamOutputFields,
    evaluateAutomationCascade,
} from '@helpers/automation-cascade.helpers';
import { ErrorCode } from '@constants/error-code';
import {
    CUSTOM_COLUMN_INT_MAX,
    CUSTOM_COLUMN_INT_MIN,
    CUSTOM_COLUMN_MAX_LENGTH,
    DIRECTIONS_MAX_LENGTH,
} from '@constants/discrepancy-limits';
import DATA_QA from '@automation/data-qa.json';
import { SelectUsersComponent } from '@shared/select/select-users/select-users.component';
import { StationCommunicationType } from '@models/station/detail';
import { UserKind } from '@models/user';

@Component({
    templateUrl: 'add-discrepancy-dialog.component.html',
    styleUrl: 'add-discrepancy-dialog.component.scss',
    providers: [StationService],
    imports: [
        ReactiveFormsModule,
        SelectComponent,
        SelectUsersComponent,
        FlatButtonComponent,
        LoaderComponent,
        InputComponent,
        DatepickerComponent,
        PageRangeFormatErrorComponent,
        InfoBoxComponent,
        TextareaComponent,
        PageRangeConflictErrorComponent,
    ],
})
export class AddDiscrepancyDialogComponent implements OnInit, OnDestroy {
    form!: FormGroup;
    formChanged = false;
    loading = false;
    rulesLoading = false;
    error = false;
    options = DocDiscrepancyCreateOptions.initial();
    fixers: SelectOption[] = [];
    pageRangeConflictData: PageConflict[] = [];
    customColumns: CustomColumn[] = [];
    customColumnOptions: Record<string, SelectOption[]> = {};
    readonly maxDirectionsLength = DIRECTIONS_MAX_LENGTH;
    readonly customColumnMaxLength = CUSTOM_COLUMN_MAX_LENGTH;
    readonly customColumnIntMin = CUSTOM_COLUMN_INT_MIN;
    readonly customColumnIntMax = CUSTOM_COLUMN_INT_MAX;
    readonly dataQa = DATA_QA;
    private initialFormValue: Record<string, unknown> = {};
    private automationRules: AutomationRule[] = [];
    private automationRuleGroups: AutomationRuleTypeGroup[] = [];
    private automationGraph: AutomationCascadeGraph =
        buildAutomationCascadeGraph([]);
    /**
     * Latest fixer request. Only its response may touch the form, so a slower
     * response of a station the user (or automation) already left is ignored.
     */
    private fixerRequestId = 0;
    /** Guards the re-run after fixer options against re-scheduling itself. */
    private reevaluatingAfterFixers = false;
    /**
     * How the running evaluation was started, so the re-run after the fixer
     * options arrive repeats it instead of falling back to another seed.
     */
    private automationSeed:
        | { kind: 'open' }
        | { kind: 'change'; changedFieldId: number | number[] }
        | null = null;
    private readonly fieldKindToControl: Partial<
        Record<AutomationFieldKind, string>
    > = {
        [AutomationFieldKind.Reason]: 'reasonId',
        [AutomationFieldKind.Station]: 'stationId',
        [AutomationFieldKind.Fixer]: 'primaryFixerId',
        [AutomationFieldKind.Directions]: 'directions',
    };
    /** Output fields the user filled in themselves, which rules may not rewrite. */
    private readonly manuallyEditedOutputFields = new Set<number>();
    private _subscriptions = new SubscriptionList() as SubscriptionListType;

    readonly data: {
        documentId: number;
        workflowId?: number;
        station?: number;
        fixer?: number;
    } = inject(MAT_DIALOG_DATA);
    private readonly formBuilder = inject(FormBuilder);
    private readonly stationService = inject(StationService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly documentService = inject(DocumentService);
    private readonly discrepancyService = inject(DiscrepancyService);
    private readonly automationService = inject(AutomationService);
    private readonly workflowService = inject(WorkflowService);
    private readonly dialogRef = inject(
        MatDialogRef<AddDiscrepancyDialogComponent>,
    );

    get pageRange() {
        return this.form.get('pageRange') as FormControl;
    }
    get stationId() {
        return this.form.get('stationId') as FormControl;
    }

    ngOnInit(): void {
        this.initForm();
        this.getFormOptions();
        this.handleFormChange();
        this.handleStationChange();
        this.loadAndApplyAutomationRules();
        this.loadCustomColumns();
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribeAllSafe();
    }

    create() {
        this.loading = true;
        this.error = false;

        this._subscriptions['add-discrepancy'] = this.discrepancyService
            .addDiscrepancyToDocument(
                this.data.documentId,
                this.buildCreatePayload(),
            )
            .subscribe({
                next: (result) => {
                    if (result.success) {
                        this.onSuccess(result.data);
                    } else {
                        this.onError(result.error);
                    }
                },
                complete: () => (this.loading = false),
            });
    }

    /**
     * Build the create payload. Static fields keep today's shape; custom
     * column values are normalized (dates -> ISO, empties dropped) and merged
     * at the top level keyed by camelCase column name, mirroring the edit flow.
     */
    private buildCreatePayload(): Record<string, unknown> {
        const { customColumns, ...formValues } = this.form.value as {
            customColumns?: Record<string, unknown>;
            [key: string]: unknown;
        };
        const normalizedCustomColumns = normalizeCustomColumnValues(
            customColumns,
            this.customColumns,
        );
        const camelCaseCustomColumns = mapKeysToCamelCase(
            normalizedCustomColumns,
        );

        const primaryFixerId = this.sanitizePrimaryFixerId(
            formValues['primaryFixerId'],
        );

        return {
            ...formValues,
            primaryFixerId,
            ...camelCaseCustomColumns,
        };
    }

    /**
     * Prevent stale fixer IDs from leaking into create payload.
     * If the current fixer value is not present in the currently loaded
     * fixer options for the selected station, force it to null.
     */
    private sanitizePrimaryFixerId(currentValue: unknown): number | null {
        if (currentValue == null || currentValue === '') {
            return null;
        }

        if (!this.isValidPrimaryFixer(currentValue)) {
            return null;
        }

        return Number(currentValue);
    }

    private isValidPrimaryFixer(value: unknown): boolean {
        if (value == null || value === '') {
            return false;
        }

        return this.fixers.some(
            (option) => String(option.value) === String(value),
        );
    }

    isCreateDisabled() {
        const hasStationValue = this.form?.controls['stationId']?.value != null;
        const hasSubmittableValue = this.formChanged || hasStationValue;
        return !this.form.valid || !hasSubmittableValue || this.loading;
    }

    cancel() {
        this.dialogRef.close({ success: false });
    }

    private initForm() {
        const { station, fixer } = this.data;

        this.form = this.formBuilder.group({
            reasonId: new FormControl(null),
            stationId: new FormControl(station, [
                Validators.required,
                optionExistsValidator(() => this.options?.station),
            ]),
            primaryFixerId: new FormControl(fixer),
            pageRange: new FormControl('', [pageRangeFormatValidator()]),
            directions: new FormControl(
                '',
                Validators.maxLength(this.maxDirectionsLength),
            ),
        });

        if (station) {
            this.getFixers(station);
        }
    }

    private getFormOptions() {
        this._subscriptions['get-create-options'] = this.documentService
            .getDocDiscrepancyCreateOptions(this.data.documentId)
            .subscribe({
                next: (data) => {
                    this.options = data;
                    this.setCustomColumnOptions(data);
                    this.stationId.updateValueAndValidity({ onlySelf: true });
                },
            });
    }

    /** Lowercase ListBox option keys for case-insensitive lookup. */
    private setCustomColumnOptions(options: DocDiscrepancyCreateOptions) {
        const map: Record<string, SelectOption[]> = {};
        Object.entries(options?.customColumns ?? {}).forEach(([key, value]) => {
            map[key.toLowerCase()] = value;
        });
        this.customColumnOptions = map;
        // ListBox outputs are skipped while the column's options are unknown,
        // and this request races `getCustomColumns`, so re-apply once they land.
        this.applyAutomationRulesOnOpen();
    }

    /**
     * Fetch the workflow's custom column metadata, build their form controls,
     * and re-snapshot the form so the new controls participate in change
     * detection (formChanged) and validity.
     */
    private loadCustomColumns() {
        if (!this.data.workflowId) return;

        this._subscriptions['custom-columns'] = this.workflowService
            .getCustomColumns(this.data.workflowId)
            .subscribe({
                next: (columns) => {
                    this.customColumns = columns ?? [];
                    this.buildCustomColumnControls();
                    // The custom column controls only exist now, so automation
                    // fields targeting them can finally be read, written and
                    // listened to.
                    this.applyAutomationRulesOnOpen();
                    this.setupAutomationRuleListeners();
                },
            });
    }

    /**
     * Add a nested `customColumns` FormGroup keyed by column name so edited
     * values flow into the create payload. Validators are intentionally omitted
     * because CustomColumn carries no field-level validation metadata.
     */
    private buildCustomColumnControls() {
        if (this.customColumns.length === 0) return;
        const group: Record<string, FormControl> = {};
        this.customColumns.forEach((col) => {
            group[col.columnName] = new FormControl(
                this.isCustomColumnDate(col) ? null : '',
            );
        });
        this.form.setControl('customColumns', this.formBuilder.group(group));
        // Re-snapshot so the new (empty) custom controls become the baseline
        // and only user edits mark the form as changed.
        this.initialFormValue = this.form.value;
    }

    isCustomColumnListBox(col: CustomColumn): boolean {
        return isCustomColumnListBox(col);
    }

    isCustomColumnDate(col: CustomColumn): boolean {
        return isCustomColumnDate(col);
    }

    getCustomColumnInputType(col: CustomColumn): string {
        return getCustomColumnInputType(col);
    }

    isCustomColumnNumber(col: CustomColumn): boolean {
        return this.getCustomColumnInputType(col) === 'number';
    }

    getCustomColumnLabel(col: CustomColumn): string {
        return getCustomColumnLabel(col);
    }

    getCustomColumnOptions(col: CustomColumn): SelectOption[] {
        return getCustomColumnOptions(col, this.customColumnOptions);
    }

    private handleFormChange() {
        this.initialFormValue = this.form.value;
        this._subscriptions['form-change'] = this.form.valueChanges.subscribe(
            () => {
                this.error = false;
                this.formChanged = this.hasFormChanged();
            },
        );
    }

    /**
     * Compare the current form value against the initial snapshot. Flat fields
     * use the original loose comparison; the nested `customColumns` group is
     * deep-compared so untouched custom fields do not mark the form as changed.
     */
    private hasFormChanged(): boolean {
        return Object.keys(this.initialFormValue).some((key) => {
            const current = this.form.value[key];
            const initial = this.initialFormValue[key];
            if (key === 'customColumns') {
                return !deepEqual(current ?? {}, initial ?? {});
            }
            return current != initial;
        });
    }

    /**
     * A fixer output can only be written once its station's fixers are known,
     * so a cascade that wrote the station asks for one full re-run here
     * (`reevaluateAutomation`) instead of dropping the fixer step.
     */
    private getFixers(
        stationId: number,
        options: { reevaluateAutomation?: boolean } = {},
    ) {
        const requestId = ++this.fixerRequestId;
        this._subscriptions['station-fixers'] = this.stationService
            .getStationFixers(stationId)
            .pipe(
                switchMap((fixers) =>
                    this.stationService
                        .getStationLookup(stationId)
                        .pipe(map((lookup) => ({ fixers, lookup }))),
                ),
            )
            .subscribe({
                next: ({ fixers, lookup }) => {
                    if (requestId !== this.fixerRequestId) return;

                    const primaryFixerCandidates =
                        lookup?.communicationType ===
                        StationCommunicationType.IN_APP
                            ? fixers.filter((i) => i.kind === UserKind.Person)
                            : fixers;

                    this.fixers = primaryFixerCandidates.map((fixer) => ({
                        value: fixer.id,
                        viewValue: `${fixer.fullName} (${fixer.email})`,
                        disabled: fixer.status === 0,
                    }));

                    const currentFixerId =
                        this.form.controls['primaryFixerId']?.value;
                    if (
                        currentFixerId != null &&
                        !this.isValidPrimaryFixer(currentFixerId)
                    ) {
                        // Keep payload consistent with the selected station:
                        // stale fixer IDs must not survive when station has
                        // no fixer (or a different fixer set).
                        this.form.patchValue(
                            { primaryFixerId: null },
                            { emitEvent: false },
                        );
                    }

                    if (options.reevaluateAutomation) {
                        this.reevaluateAfterFixers();
                    }
                },
            });
    }

    /** One full re-run of the evaluation, now that the fixers are known. */
    private reevaluateAfterFixers(): void {
        const seed = this.automationSeed;
        if (this.reevaluatingAfterFixers || !seed) return;

        this.reevaluatingAfterFixers = true;
        try {
            if (seed.kind === 'open') {
                this.applyAutomationRulesOnOpen();
            } else {
                this.applyAutomationRules(seed.changedFieldId);
            }
        } finally {
            this.reevaluatingAfterFixers = false;
        }
    }

    private handleStationChange(): void {
        this._subscriptions['station-change'] = this.form.controls[
            'stationId'
        ].valueChanges.subscribe((change) => {
            const stationFieldIds = this.stationInputFieldIds();
            const inputFields = automationInputFields(this.automationRules);
            const stationOutputFieldIds = automationOutputFieldIdsOfKind(
                this.automationRules,
                AutomationFieldKind.Station,
            );
            stationFieldIds.forEach((fieldId) =>
                this.clearManuallyEditedOutputsForInput(fieldId),
            );
            stationOutputFieldIds.forEach((fieldId) =>
                this.manuallyEditedOutputFields.add(fieldId),
            );
            stationFieldIds.forEach((fieldId) => {
                const field = inputFields.get(fieldId);
                if (!field) return;

                automationOutputFieldIdsSharingTarget(
                    this.automationRules,
                    field,
                ).forEach((outputId) =>
                    this.manuallyEditedOutputFields.add(outputId),
                );
            });

            this.form.patchValue(
                { primaryFixerId: null },
                { emitEvent: false },
            );
            if (change) {
                // Fixer outputs of the chain can only be written against the
                // new station's fixers, so the evaluation is repeated once
                // they arrive.
                this.getFixers(change, { reevaluateAutomation: true });
            } else {
                // Invalidate the in-flight request too, otherwise its response
                // would repopulate the fixers of the station just cleared.
                this.fixerRequestId++;
                this._subscriptions.unsubscribeSafe('station-fixers');
                this.fixers = [];
            }
            this.applyAutomationRules(stationFieldIds);
        });
    }

    private loadAndApplyAutomationRules(): void {
        if (!this.data.workflowId) return;

        this.rulesLoading = true;
        this._subscriptions['load-rules'] = this.automationService
            .getAutomationRules(this.data.workflowId)
            .subscribe({
                next: (groups) => {
                    this.automationRuleGroups = groups;
                    this.automationRules = flattenAutomationRules(groups);
                    this.automationGraph = buildAutomationCascadeGraph(
                        groups,
                        (fieldId) =>
                            automationFieldIdsSharingTarget(
                                this.automationRules,
                                fieldId,
                            ),
                    );
                    this.applyAutomationRulesOnOpen();
                    this.setupAutomationRuleListeners();
                },
                complete: () => (this.rulesLoading = false),
            });
    }

    /**
     * On initial open, only the station value is considered as a "pre-provided"
     * input. The fixer field may be pre-filled from the parent component
     * (sister-discrepancy flow), but it must not trigger fixer-based rules;
     * those should only fire when the user explicitly picks a fixer.
     * All other input kinds are intentionally absent so their rules cannot match.
     */
    private applyAutomationRulesOnOpen(): void {
        const stationFieldIds = this.stationInputFieldIds();
        const stationValue = this.form.controls['stationId']?.value;
        const stationOnlyValues = new Map<number, string | null>(
            stationFieldIds.map((fieldId) => [
                fieldId,
                stationValue != null ? stationValue.toString() : null,
            ]),
        );

        this.automationSeed = { kind: 'open' };
        // Matching sees the station only, but "would this write change
        // anything" is still decided against the values in the form, so a
        // rule blanking a prefilled field does clear it. Without a station
        // field the cascade still runs, for the rule types without inputs.
        // Opening fills the form as a whole, so the rule types matching
        // whatever the values are contribute their defaults here.
        this.runAutomationCascade(
            stationFieldIds,
            stationOnlyValues,
            this.buildCurrentValues(),
            true,
        );
    }

    /** Every input field id the station control feeds. */
    private stationInputFieldIds(): number[] {
        return automationInputFieldIdsOfKind(
            this.automationRules,
            AutomationFieldKind.Station,
        );
    }

    private buildCurrentValues(): Map<number, string | null> {
        // Read directly from each FormControl. When this is invoked from a
        // child control's `valueChanges` subscription the parent
        // `form.value` has not yet been recomputed, so it can be stale; the
        // child control's `.value` is always up to date.
        const values = new Map<number, string | null>();
        const fields = new Map([
            ...automationInputFields(this.automationRules),
            ...automationOutputFields(this.automationRules),
        ]);

        fields.forEach((field, fieldId) => {
            const target = this.resolveAutomationTarget(field);
            if (!target) return;
            values.set(
                fieldId,
                toAutomationRuleValue(
                    target,
                    target.control.value,
                    this.automationOptionContext(field, target),
                ),
            );
        });

        return values;
    }

    private applyAutomationRules(changedFieldId: number | number[]): void {
        this.automationSeed = { kind: 'change', changedFieldId };
        this.runAutomationCascade(changedFieldId, this.buildCurrentValues());
    }

    /**
     * Evaluate the chain the changed field starts and write everything it
     * derives, so a field filled by one rule type feeds the rule types
     * consuming it in the same pass.
     */
    private runAutomationCascade(
        changedFieldId: number | number[],
        currentValues: Map<number, string | null>,
        comparisonValues?: Map<number, string | null>,
        includeUnconditionalRuleTypes?: boolean,
    ): void {
        if (this.automationRuleGroups.length === 0) return;

        const writes = evaluateAutomationCascade({
            groups: this.automationRuleGroups,
            graph: this.automationGraph,
            changedFieldId,
            currentValues,
            comparisonValues,
            includeUnconditionalRuleTypes,
            host: {
                matchRules: (group, values) =>
                    this.automationService.matchRules(group, values),
                prepareWrite: (fieldId, ruleValue) =>
                    this.prepareAutomationWrite(fieldId, ruleValue),
                isWriteSuppressed: (fieldId) =>
                    this.manuallyEditedOutputFields.has(fieldId),
                fieldIdsSharingTarget: (fieldId) =>
                    automationFieldIdsSharingTarget(
                        this.automationRules,
                        fieldId,
                    ),
            },
        });
        if (writes.length === 0) return;

        const outputFields = automationOutputFields(this.automationRules);
        const patchValues: Record<string, unknown> = {};
        let columnPatched = false;

        writes.forEach(({ fieldId, controlValue }) => {
            const field = outputFields.get(fieldId);
            const target = field && this.resolveAutomationTarget(field);
            if (!field || !target) return;

            if (target.column) {
                target.control.patchValue(controlValue, { emitEvent: false });
                columnPatched = true;
                return;
            }
            patchValues[this.fieldKindToControl[field.fieldKind]!] =
                controlValue;
        });

        const standardPatched = Object.keys(patchValues).length > 0;
        if (standardPatched)
            this.form.patchValue(patchValues, { emitEvent: false });

        // The cascade patches without events, so the change flag the Create
        // button depends on is recomputed here. Values derived on open stay
        // out of it: they are defaults, not a change made in the dialog.
        if (
            (standardPatched || columnPatched) &&
            this.automationSeed?.kind === 'change'
        )
            this.formChanged = this.hasFormChanged();

        if (!standardPatched) return;

        const stationPatched = typeof patchValues['stationId'] === 'number';
        if (stationPatched) {
            // The fixer options of the new station are unknown here, so the
            // fixer steps of the chain are evaluated once they arrive.
            this.getFixers(patchValues['stationId'] as number, {
                reevaluateAutomation: true,
            });
        }
    }

    /** Rule value coerced for the field's control, or `null` to keep it. */
    private prepareAutomationWrite(
        fieldId: number,
        ruleValue: string,
    ): AutomationPreparedWrite | null {
        const field = automationOutputFields(this.automationRules).get(fieldId);
        const target = field && this.resolveAutomationTarget(field);
        if (!field || !target) return null;

        if (ruleValue.trim() === '') {
            // Conversion cannot express "no value", so a blank output is
            // mapped to the empty value of the control itself.
            return {
                controlValue: this.emptyControlValue(field.fieldKind, target),
                canonical: null,
            };
        }

        const optionContext = this.automationOptionContext(field, target);
        const converted = target.column
            ? toCustomColumnControlValue(
                  target.column,
                  ruleValue,
                  this.getCustomColumnOptions(target.column),
                  field.valueKind,
              )
            : this.convertValue(field.fieldKind, ruleValue, optionContext);
        if (converted === undefined) return null;

        return {
            controlValue: converted,
            canonical: toAutomationRuleValue(target, converted, optionContext),
        };
    }

    /**
     * What the rules of a field mean by an option value, and the options this
     * dialog offers for it.
     */
    private automationOptionContext(
        field: AutomationRuleField,
        target: AutomationTarget,
    ): AutomationOptionContext {
        return {
            valueKind: field.valueKind,
            options: target.column
                ? this.getCustomColumnOptions(target.column)
                : this.standardFieldOptions(field.fieldKind),
        };
    }

    /** Options the dialog offers for a standard field kind, if it has any. */
    private standardFieldOptions(
        fieldKind: AutomationFieldKind,
    ): SelectOption[] {
        if (fieldKind === AutomationFieldKind.Reason)
            return this.options.reason ?? [];
        if (fieldKind === AutomationFieldKind.Station)
            return this.options.station ?? [];

        return [];
    }

    private resolveAutomationTarget(
        field: AutomationRuleField,
    ): AutomationTarget | null {
        return resolveAutomationTarget(
            field,
            this.form,
            this.fieldKindToControl,
            this.customColumns,
        );
    }

    private setupAutomationRuleListeners(): void {
        automationInputFields(this.automationRules).forEach(
            (field, fieldId) => {
                // Station changes are already handled in handleStationChange,
                // which itself triggers rule re-evaluation.
                if (field.fieldKind === AutomationFieldKind.Station) return;

                const target = this.resolveAutomationTarget(field);
                if (!target) return;

                // Rule types can declare their own field for the same
                // control, so its change triggers all of them at once and
                // only the first of them carries the subscription.
                const sharedFieldIds = automationInputFieldIdsSharingTarget(
                    this.automationRules,
                    field,
                );
                if (sharedFieldIds[0] !== fieldId) return;

                // Output fields the same control writes to, whatever field id
                // their rule type declares for it.
                const sharedOutputFieldIds =
                    automationOutputFieldIdsSharingTarget(
                        this.automationRules,
                        field,
                    );

                this._subscriptions[`automation-input-${fieldId}`] =
                    target.control.valueChanges.subscribe(() => {
                        sharedFieldIds.forEach((sharedId) =>
                            this.clearManuallyEditedOutputsForInput(sharedId),
                        );
                        // A control that is an input as well as an output keeps
                        // the value the user just picked, so its protection is
                        // set after the clearing above.
                        sharedOutputFieldIds.forEach((outputId) =>
                            this.manuallyEditedOutputFields.add(outputId),
                        );
                        this.applyAutomationRules(sharedFieldIds);
                    });
            },
        );

        automationOutputFields(this.automationRules).forEach(
            (field, fieldId) => {
                const target = this.resolveAutomationTarget(field);
                if (!target) return;

                // The cascade patches without events, so only a value the user
                // entered reaches this listener.
                this._subscriptions[`automation-output-${fieldId}`] =
                    target.control.valueChanges.subscribe(() =>
                        this.manuallyEditedOutputFields.add(fieldId),
                    );
            },
        );
    }

    /**
     * When an input changes, every output its change can re-derive becomes
     * writable again, transitively along the chain. The changed field itself
     * keeps its protection: the user entered it, so its own chain must not
     * derive it again.
     */
    private clearManuallyEditedOutputsForInput(inputFieldId: number): void {
        downstreamOutputFields(
            this.automationRuleGroups,
            inputFieldId,
            (fieldId) =>
                automationFieldIdsSharingTarget(this.automationRules, fieldId),
        ).forEach((fieldId) => {
            if (fieldId === inputFieldId) return;
            this.manuallyEditedOutputFields.delete(fieldId);
        });
    }

    private convertValue(
        fieldKind: AutomationFieldKind,
        value: string,
        optionContext: AutomationOptionContext,
    ): string | number | undefined {
        // A name is the option of whichever repository reads the same, so it is
        // resolved against the options of this dialog rather than parsed.
        if (isNameBasedValueKind(optionContext.valueKind)) {
            return findOptionByRuleValue(
                optionContext.options ?? [],
                value,
                optionContext.valueKind,
            )?.value;
        }

        if (this.isSelectKind(fieldKind)) {
            const optionId = Number(value);
            return Number.isNaN(optionId) ? undefined : optionId;
        }

        return value;
    }

    /** Value that leaves the field's control empty. */
    private emptyControlValue(
        fieldKind: AutomationFieldKind,
        target: AutomationTarget,
    ): string | number | Date | null {
        if (target.column)
            return isCustomColumnDate(target.column) ||
                isCustomColumnListBox(target.column)
                ? null
                : '';

        return this.isSelectKind(fieldKind) ? null : '';
    }

    private isSelectKind(fieldKind: AutomationFieldKind): boolean {
        return [
            AutomationFieldKind.Reason,
            AutomationFieldKind.Station,
            AutomationFieldKind.Fixer,
            AutomationFieldKind.SecondaryFixer,
        ].includes(fieldKind);
    }

    private onSuccess(data: DiscrepancyDetail) {
        this.snackbarService.success({
            variant: 'success',
            header: 'Discrepancy Is Created',
        });
        this.dialogRef.close({
            success: true,
            discId: data.id,
        });
    }

    private onError(error: ErrorResponse) {
        if (error.code === ErrorCode.PAGE_CONFLICT) {
            this.pageRange.setErrors({
                pageRangeConflict: 'pageRangeConflict',
            });
            this.pageRangeConflictData = error.data ?? [];
        }
        this.error = true;
    }
}
